import React from 'react';

type UseFramePlaceholderHeightArgs = {
  frameId: string;
  isContentRevealed: boolean;
  getFrameDOMElement: (frameId: string) => HTMLElement | null;
};

/**
 * Tracks the frame element's rendered height while its content is revealed,
 * and returns the last tracked height once the content is hidden.
 *
 * This is used in cases where frame content is hidden but we want to maintain
 * its last known height while it was revealed.
 *
 * Note that this hook silently observes the height and would not trigger a re-render
 * when the observed height changes. If you need to track the measured height of the frame
 * and trigger re-rendering in the UI, see `useMeasuredFrameSize`.
 *
 * @returns `null` while the content is revealed, or when no height was ever
 * measured (e.g. the frame's content was never revealed). Callers should fall
 * back to their default hidden-frame sizing in that case.
 */
export const useFramePlaceholderHeight = ({
  frameId,
  isContentRevealed,
  getFrameDOMElement,
}: UseFramePlaceholderHeightArgs): number | null => {
  const lastRevealedHeightRef = React.useRef<number | null>(null);

  React.useLayoutEffect(() => {
    if (!isContentRevealed) return;

    const frameElement = getFrameDOMElement(frameId);
    if (!frameElement) return;

    const resizeObserver = new ResizeObserver(([entry]) => {
      if (entry) lastRevealedHeightRef.current = entry.contentRect.height;
    });

    resizeObserver.observe(frameElement);
    return () => resizeObserver.disconnect();
  }, [frameId, isContentRevealed, getFrameDOMElement]);

  return isContentRevealed ? null : lastRevealedHeightRef.current;
};
